import {
  Dom,
  ProxyChain,
  type ChainablePromise,
  nameof as _nameof
} from './utils/index.js';

export abstract class ApiResolver<
  TUninitialized extends object | undefined = object | undefined,
  TInitialized extends
    NonNullable<TUninitialized> = NonNullable<TUninitialized>,
  TLoaded extends TInitialized = TInitialized,
  TWindow extends Record<string, unknown> = Record<string, unknown>
> {
  get proxy() {
    if (this.#proxy) return this.#proxy;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    async function Api<TReturn>(
      executor: ApiResolver.Executor<TReturn, TLoaded>,
      ...abortSignals: ReadonlyArray<AbortSignal>
    ): Promise<TReturn>;
    async function Api(
      ...abortSignals: ReadonlyArray<AbortSignal>
    ): Promise<TLoaded>;
    async function Api<TReturn>(
      executorOrAbortSignal?:
        | ApiResolver.Executor<TReturn, TLoaded>
        | AbortSignal,
      ...abortSignals: ReadonlyArray<AbortSignal>
    ): Promise<TReturn | TLoaded> {
      if (typeof executorOrAbortSignal === 'function') {
        return await self.execute<TReturn>(
          executorOrAbortSignal,
          ...abortSignals
        );
      }

      if (executorOrAbortSignal) {
        abortSignals = [executorOrAbortSignal, ...abortSignals];
      }

      return await self.execute<TLoaded>(
        ((globalRef) => globalRef) as ApiResolver.Executor<TLoaded, TLoaded>,
        ...abortSignals
      );
    }

    return (this.#proxy = ProxyChain.create(
      Object.setPrototypeOf(Api, this) as this & typeof Api
    ) as unknown as ApiResolver.Proxy<this, TLoaded>);
  }

  #proxy: ApiResolver.Proxy<this, TLoaded> | null = null;

  /**
   * The name of the global property used to access the API on `window`.
   *
   * @example
   * 'googletag'
   */
  protected abstract readonly globalPropertyName: Extract<
    keyof TWindow,
    string
  >;

  /**
   * Handles how commands are queued for the API. This is typically done by
   * pushing the command to an array on the global reference. Execution of
   * the command is done by the API itself, which necessarily means that the
   * API must be fully loaded before the command is executed. This is why the
   * {@linkcode execute} method needs to be async - because we don't know when
   * the API will have completed loading.
   *
   * @example
   * googletag.cmd.push(executor);
   */
  protected abstract enqueue(
    globalRef: TInitialized | TLoaded,
    executor: () => void
  ): void;

  /**
   * Checks if the API is enabled. This is typically done by checking a feature
   * flag or some other condition. If the API is not enabled, invoking the
   * {@linkcode execute} method will throw a {@linkcode ApiResolver.DisabledError}.
   */
  protected abstract isEnabled(): boolean | PromiseLike<boolean>;

  /**
   * Checks if the API is initialized. This is typically done by checking if
   * the command array is defined on the global reference.
   *
   * @example
   * window.googletag?.cmd !== undefined
   */
  protected abstract isInitialized(
    window: typeof globalThis.window
  ): window is ApiResolver.Window.AsInitialized<
    TWindow,
    typeof this.globalPropertyName,
    TUninitialized,
    TInitialized,
    TLoaded
  >;

  /**
   * Checks if the API is fully loaded and ready to use. This is typically done
   * by checking if certain properties or methods are available on the global
   * reference.
   *
   * @example
   * window.googletag.apiReady
   */
  protected abstract isLoaded(
    globalRef: TInitialized | TLoaded
  ): globalRef is TLoaded;

  /**
   * Initializes the API. This is typically done by setting up the global
   * reference on `window` and creating the command array on the global reference.
   *
   * @example
   * window.googletag ??= {};
   * window.googletag.cmd ??= [];
   */
  protected abstract initialize(
    window: ApiResolver.Window.AsUninitialized<
      TWindow,
      typeof this.globalPropertyName,
      TUninitialized
    >
  ): asserts window is ApiResolver.Window.AsInitialized<
    TWindow,
    typeof this.globalPropertyName,
    TUninitialized,
    TInitialized,
    TLoaded
  >;

  /**
   * Synchronously resolves the global reference if it has already been
   * initialized and loaded, i.e. it is of type {@linkcode TLoaded}. Otherwise,
   * an {@linkcode ApiResolver.GlobalReferenceError} is thrown.
   */
  protected get loadedGlobalRef() {
    if (!this.#loadedGlobalRef) {
      throw new ApiResolver.GlobalReferenceError(this.globalPropertyName);
    }

    return this.#loadedGlobalRef;
  }

  /**
   * The global reference is a property on the `window` object. This is typically
   * a library or API that is loaded on the page. It's used to access the API and
   * execute commands. It's of type {@linkcode TLoaded} when the API is fully
   * loaded and ready to use, otherwise `null`. It's cached here for swift
   * consecutive access - this avoids invoking the command array unnecessarily.
   */
  #loadedGlobalRef: TLoaded | null = null;

  /**
   * Executes the provided executor function with the resolved global reference.
   * If the global reference is not yet initialized or loaded, it will be
   * initialized and loaded before the executor function is executed.
   */
  async execute<TReturn>(
    executor: ApiResolver.Executor<TReturn, TLoaded>,
    ...abortSignals: ReadonlyArray<AbortSignal>
  ): Promise<TReturn> {
    for (const abortSignal of abortSignals) abortSignal.throwIfAborted();

    const globalRef = await this.#resolve(...abortSignals);
    const { promise, resolve, reject } = Promise.withResolvers<TReturn>();

    this.enqueue(globalRef, () => {
      try {
        for (const abortSignal of abortSignals) abortSignal.throwIfAborted();
        resolve(executor(globalRef, ...abortSignals));
      } catch (error) {
        reject(error);
      }
    });

    return await promise;
  }

  /**
   * Checks if the API has not been initialized yet. This is typically done by
   * checking if the global reference is `undefined` or if the command array is
   * not initialized.
   *
   * @example
   * window.googletag?.cmd === undefined
   */
  protected isUninitialized(
    window: typeof globalThis.window
  ): window is ApiResolver.Window.AsUninitialized<
    TWindow,
    typeof this.globalPropertyName,
    TUninitialized
  > {
    return !this.isInitialized(window);
  }

  /** Initializes the API if it has not been initialized yet. */
  #tryInitialize(
    window: typeof globalThis.window
  ): asserts window is ApiResolver.Window.AsInitialized<
    TWindow,
    typeof this.globalPropertyName,
    TUninitialized,
    TInitialized,
    TLoaded
  > {
    if (this.isUninitialized(window)) this.initialize(window);
  }

  /**
   * Resolves the global reference. If the global reference is not yet initialized
   * or loaded, it will be initialized and we'll wait for it to be fully loaded
   * before resolving the promise.
   */
  async #resolve(...abortSignals: ReadonlyArray<AbortSignal>) {
    Dom.assertIsAvailable();
    this.#assertIsEnabled();

    for (const abortSignal of abortSignals) {
      abortSignal.throwIfAborted();
    }

    if (this.#loadedGlobalRef) return this.#loadedGlobalRef;

    const { promise, resolve, reject } = Promise.withResolvers<TLoaded>();

    try {
      this.#tryInitialize(window);

      const globalRef = window[this.globalPropertyName];

      for (const abortSignal of abortSignals) {
        abortSignal.addEventListener('abort', reject);
      }

      this.enqueue(globalRef, () => {
        try {
          for (const abortSignal of abortSignals) {
            abortSignal.throwIfAborted();
          }

          this.#assertIsLoaded(globalRef);
          resolve((this.#loadedGlobalRef ??= globalRef));
        } catch (error) {
          reject(error);
        }
      });
    } catch (error) {
      reject(error);
    } finally {
      for (const abortSignal of abortSignals) {
        abortSignal.removeEventListener('abort', reject);
      }
    }

    return await promise;
  }

  /**
   * Asserts that the provided global reference is loaded. Otherwise, it throws
   * one of the following errors:
   *
   * - {@linkcode Dom.UnavailableError} if the DOM is not available.
   * - {@linkcode ApiResolver.DisabledError} if the API is not enabled.
   * - {@linkcode ApiResolver.UnmetConditionError} if the conditions for a loaded
   *   global reference are not met.
   */
  #assertIsLoaded(
    globalRef: TInitialized | TLoaded
  ): asserts globalRef is TLoaded {
    Dom.assertIsAvailable();
    this.#assertIsEnabled();

    if (!this.isLoaded(globalRef)) {
      throw new ApiResolver.UnmetConditionError(this.globalPropertyName);
    }
  }

  /**
   * Asserts that the API is enabled. Throws a {@linkcode DisabledError} if the
   * API is not enabled.
   */
  #assertIsEnabled() {
    if (!this.isEnabled()) {
      throw new ApiResolver.DisabledError(this.globalPropertyName);
    }
  }
}

export namespace ApiResolver {
  const nameof = _nameof.prefix({ ApiResolver });

  export function getGlobalPropertyNames<const T extends string>(
    globalPropertyName: T
  ) {
    return {
      globalPropertyName,
      unsafePropertyName: `unsafe_${globalPropertyName}`
    } as const;
  }

  export type Proxy<TApiResolver, TLoaded extends object> = TApiResolver & {
    <TReturn>(
      executor: ApiResolver.Executor<TReturn, TLoaded>,
      ...abortSignals: ReadonlyArray<AbortSignal>
    ): Promise<TReturn>;
    (...abortSignals: ReadonlyArray<AbortSignal>): ChainablePromise<TLoaded>;
  };

  export type Executor<TReturn, TLoaded extends object> = [TReturn] extends [
    PromiseLike<unknown>
  ]
    ? never
    : (
        globalRef: TLoaded,
        ...abortSignals: ReadonlyArray<AbortSignal>
      ) => TReturn;

  export namespace Window {
    export type AsUninitialized<
      TWindow extends Record<string, unknown>,
      TGlobalPropertyName extends string,
      TUninitialized extends object | undefined
    > = typeof globalThis.window & {
      [TKey in keyof TWindow as TKey extends TGlobalPropertyName
        ? TKey
        : never]: TUninitialized;
    };

    export type AsInitialized<
      TWindow extends Record<string, unknown>,
      TGlobalPropertyName extends string,
      TUninitialized extends object | undefined,
      TInitialized extends NonNullable<TUninitialized>,
      TLoaded extends TInitialized
    > = typeof globalThis.window &
      TWindow & {
        [TKey in TGlobalPropertyName]: TInitialized | TLoaded;
      };
  }

  export class GlobalReferenceError extends ReferenceError {
    static {
      this.prototype.name = nameof({ GlobalReferenceError });
    }

    constructor(globalPropertyName: string) {
      super(
        `Attempted to access a fully loaded 'window.${globalPropertyName}' that's not ready.`
      );
    }
  }

  /**
   * Error thrown when the command array is being executed but the API is not
   * yet fully loaded and ready. This typically means that the API has not been
   * properly initialized or loaded.
   */
  export class UnmetConditionError extends Error {
    static {
      this.prototype.name = nameof({ UnmetConditionError });
    }

    constructor(globalPropertyName: string) {
      super(
        `Failed resolving reference 'window.${globalPropertyName}' due to unmet condition.`
      );
    }
  }

  /**
   * Error thrown when the API is not enabled. This typically means that the
   * feature flag is not enabled.
   */
  export class DisabledError extends Error {
    static {
      this.prototype.name = nameof({ DisabledError });
    }

    constructor(globalPropertyName: string) {
      super(
        `Failed attempt to invoke 'window.${globalPropertyName}' due to being disabled!`
      );
    }
  }
}
